import Footer from "./footer";
import SEO from "../seo/SEO";
import { SEO_PAGES, getBreadcrumbSchema } from "../seo/seoConfig";

export default function PrivacyPolicy() {
    const seo = SEO_PAGES.privacy;
    return (
        <>
            <SEO
                {...seo}
                schemas={[
                    getBreadcrumbSchema([
                        { name: "Home", path: "/" },
                        { name: "Privacy Policy", path: "/privacy-policy" },
                    ]),
                ]}
            />
           <div className="min-h-screen mt-20" id="privacy-policy">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-8">
                        <span className="inline-block bg-emerald-50 text-emerald-700 text-sm px-3 py-1 rounded-full font-medium mb-4">
                            Privacy Policy
                        </span>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[62.86px] font-400 leading-tight tracking-tight mb-6">
                            <span className="text-emerald-500">Privacy </span>
                            <span className="bg-gradient-to-r from-emerald-400 to-blue-600 bg-clip-text text-transparent">Policy</span>
                        </h1>
                        <p className="text-gray-600 mb-8">
                            At PyCray Technologies, we are committed to protecting your privacy and ensuring the security of your personal
                            information. This Privacy Policy explains how we collect, use, and safeguard your data when you interact with our
                            website or services.
                        </p>
                    </div>

                    <div className="space-y-8">
                        <section>
                            <h2 className="text-lg font-semibold mb-3">1. Information We Collect</h2>
                            <p className="text-gray-600 mb-2">We collect the following types of information:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="text-gray-600">
                                    <span className="font-medium">Personal Information:</span> Name, email address, phone number, and other contact
                                    details you provide when using our services or contacting us.
                                </li>
                                <li className="text-gray-600">
                                    <span className="font-medium">Usage Data:</span> Information about how you use our website, such as IP address,
                                    browser type, and pages visited.
                                </li>
                                <li className="text-gray-600">
                                    <span className="font-medium">Cookies and Tracking Technologies:</span> Data collected through cookies to enhance
                                    your browsing experience.
                                </li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">2. How We Use Your Information</h2>
                            <p className="text-gray-600 mb-2">We use your information for:</p>
                            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                                <li>Providing and improving our services.</li>
                                <li>Responding to inquiries and support requests.</li>
                                <li>Sending updates, promotional materials, or other communications (you can opt out at any time).</li>
                                <li>Analyzing website usage to enhance functionality.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">3. How We Share Your Information</h2>
                            <p className="text-gray-600 mb-2">
                                We do not sell or rent your personal information. However, we may share your data with:
                            </p>
                            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                                <li>
                                    <span className="font-medium">Service Providers:</span> Third-party vendors that assist in delivering our
                                    services, such as payment processors or hosting providers.
                                </li>
                                <li>
                                    <span className="font-medium">Legal Obligations:</span> Authorities or regulators if required by law or to
                                    protect our legal rights.
                                </li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">4. Cookies and Tracking Technologies</h2>
                            <p className="text-gray-600 mb-2">We use cookies to enhance your experience, including:</p>
                            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                                <li>Essential Cookies: Required for website functionality.</li>
                                <li>Performance Cookies: Help us understand how visitors interact with the site.</li>
                                <li>Preference Cookies: Remember your preferences and settings.</li>
                            </ul>
                            <p className="text-gray-600 mt-2">You can manage your cookie preferences through your browser settings.</p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">5. Data Security</h2>
                            <p className="text-gray-600">
                                We take reasonable measures to protect your information from unauthorized access, alteration, disclosure, or
                                destruction. However, no method of transmission or storage is completely secure.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">6. Your Rights</h2>
                            <p className="text-gray-600 mb-2">You have the right to:</p>
                            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                                <li>Access the personal data we hold about you.</li>
                                <li>Request corrections or updates to your information.</li>
                                <li>Request deletion of your data (subject to legal or contractual obligations).</li>
                                <li>Opt-out of marketing communications.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">7. Third-Party Links</h2>
                            <p className="text-gray-600">
                                Our website may contain links to third-party websites. We are not responsible for the privacy practices or
                                content of those sites.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">8. Children's Privacy</h2>
                            <p className="text-gray-600">
                                Our website is not intended for use by children under the age of 13. We do not knowingly collect personal
                                information from children.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">9. Changes to This Policy</h2>
                            <p className="text-gray-600">
                                We may update this Privacy Policy periodically. Any changes will be posted on this page with an updated
                                effective date.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold mb-3">10. Contact Us</h2>
                            <p className="text-gray-600 mb-2">If you have questions or concerns about our Privacy Policy, please contact us:</p>
                            <div className="text-gray-600 ml-4">
                                <p>Email: info@pycray.com</p>
                                <p>Address: B-7, First Floor, B Block, Sector 2, Noida, Uttar Pradesh, India 201301</p>
                            </div>
                        </section>

                        <p className="text-gray-600 italic mt-8">
                            By using our website, you consent to the terms outlined in this Privacy Policy.
                        </p>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
}
